import { useEffect, useState } from 'react'
import { api } from '@pilot-hub/api-client'

export interface TruppInfo { key: string; name: string; typ: string; fuehrerId: number | null; mitglieder: number[] }
export interface TruppPerson {
  id: number
  name: string
  /** alle Funktionen des Benutzers (PILOT, CO_PILOT, LUFTRAUM_BEOBACHTER, ...) */
  funktionen: string[]
  /** Funktion im Kräfte Management (besatzung[].funktion) */
  kmFunktion: string | null
  fahrzeug: string | null
}

/** Trupps des Einsatzes (Kräfte Management) mit Namen, Funktionen und Fahrzeug der Mitglieder; aktualisiert sich alle 20 s. */
export function useEinsatzTrupps(einsatzId: number | string | null | undefined) {
  const [trupps, setTrupps] = useState<TruppInfo[]>([])
  const [personen, setPersonen] = useState<Map<number, TruppPerson>>(new Map())
  const [nutzerListe, setNutzerListe] = useState<Map<number, { name: string; funktionen: string[] }>>(new Map())

  useEffect(() => {
    const id = Number(einsatzId)
    if (!id) { setTrupps([]); return }
    let abbruch = false
    const laden = async () => {
      try {
        const [km, nutzer, fz] = await Promise.all([
          api.get(`/kraefte-management/${id}`),
          api.get('/nutzer', { params: { size: 500 } }),
          api.get('/fahrzeuge', { params: { size: 500 } }).catch(() => ({ data: { items: [] } })),
        ])
        const data = km.data?.data ?? {}
        const liste: TruppInfo[] = (data.trupps ?? []).filter((t: any) => (t?.mitglieder ?? []).length > 0).map((t: any) => ({
          key: String(t.key), name: t.name ?? '', typ: t.typ ?? 'drohnentrupp',
          fuehrerId: t.fuehrerId ?? null, mitglieder: (t.mitglieder ?? []).map(Number),
        }))
        const fzMap = new Map<number, string>(((fz.data?.items ?? []) as any[]).map(f => [f.id, f.rufzeichen || f.fahrzeugtyp || f.kennzeichen || `Fahrzeug ${f.id}`]))
        const nutzerMap = new Map<number, any>(((nutzer.data?.items ?? []) as any[]).map(n => [n.id, n]))
        const map = new Map<number, TruppPerson>()
        const ids = new Set<number>(liste.flatMap(t => t.mitglieder))
        for (const b of (data.bloecke ?? [])) for (const r of (b.besatzung ?? [])) {
          if (r.nutzerId == null) continue
          const n = nutzerMap.get(r.nutzerId)
          map.set(r.nutzerId, {
            id: r.nutzerId,
            name: n ? (`${n.vorname ?? ''} ${n.nachname ?? ''}`.trim() || n.benutzername) : `Benutzer ${r.nutzerId}`,
            funktionen: n?.funktionen ?? [], kmFunktion: r.funktion || null,
            fahrzeug: b.fahrzeugId != null ? (fzMap.get(Number(b.fahrzeugId)) ?? null) : null,
          })
        }
        // Reserve (Wache / Einsatz) zählt ebenfalls zu den Einsatzkräften
        const reserve = [...(data.reserveWache ?? []), ...(data.einsatzReserve ?? []).map((e: any) => e?.nutzerId)].filter((x: any) => x != null).map(Number)
        for (const i of [...ids, ...reserve]) if (!map.has(i)) {
          const n = nutzerMap.get(i)
          map.set(i, { id: i, name: n ? (`${n.vorname ?? ''} ${n.nachname ?? ''}`.trim() || n.benutzername) : `Benutzer ${i}`, funktionen: n?.funktionen ?? [], kmFunktion: null, fahrzeug: null })
        }
        const alle = new Map<number, { name: string; funktionen: string[] }>()
        for (const [i, n] of nutzerMap) alle.set(i, { name: `${n.vorname ?? ''} ${n.nachname ?? ''}`.trim() || n.benutzername, funktionen: n.funktionen ?? [] })
        if (!abbruch) { setTrupps(liste); setPersonen(map); setNutzerListe(alle) }
      } catch { /* Anzeige bleibt wie sie war */ }
    }
    laden()
    const t = window.setInterval(laden, 20000)
    return () => { abbruch = true; window.clearInterval(t) }
  }, [einsatzId])

  return { trupps, personen, nutzerListe }
}

/** Anzeigename eines Trupptyps (taktisches Zeichen der Gruppe "Einheiten") */
export const TRUPP_TYP_LABEL: Record<string, string> = {
  drohnentrupp: 'Drohnentrupp', rettungshund: 'Rettungshundetrupp', sanitaetsgruppe: 'Sanitätsgruppe',
  aerztliche_versorgung: 'Ärztliche Versorgung', rettungsdienst: 'Rettungsdienst', formation_einsatz: 'Formation Einsatz',
  betreuung: 'Betreuung', psnv: 'PSNV', verpflegung: 'Verpflegung', technik_sicherheit: 'Technik und Sicherheit', brandschutz: 'Brandschutz',
}
